import { FormControl, Typography, Select, MenuItem, Chip, SelectChangeEvent, InputLabel, Stack, SvgIconTypeMap, TextField, Divider, Autocomplete } from '@mui/material';
import { OverridableComponent } from '@mui/types';
import React, { useState } from 'react';

interface DropdownSelectionProps {
    currentValue: string;
    selectionValues: { value: string; label: string, color?: string, icon?: OverridableComponent<SvgIconTypeMap<{}, "svg">> & {
        muiName: string;
    } }[];
    handleOnChange: (event: SelectChangeEvent<string>, child: React.ReactNode) => void;
    sxObject?: any;
    labelSx?: any;
    variant?: 'filled' | 'outlined';
    dropDownLabel: string;
    showHeader?: boolean;
    showChip?: boolean;
    showError?: boolean;
    helperText?: string;
    isReadOnly?: boolean;
    getAutoComplete?: boolean;
}

const DropdownSelection: React.FC<DropdownSelectionProps> = ({
    currentValue,
    selectionValues,
    handleOnChange,
    sxObject,
    variant,
    dropDownLabel,
    showHeader,
    labelSx,
    showChip,
    showError = false,
    helperText = '',
    isReadOnly = false,
    getAutoComplete = false
}) => {
    const getMenuItems = () => {
        return selectionValues.map((value) => {
            return <MenuItem sx={{p: 2}} key={value.value} value={value.value} disabled={value.value === ''}>
                {showChip ? <Chip sx={{ display: 'flex', backgroundColor: value.color || 'default'}} label={value.label} color='primary'/> : 
                <Stack direction="row" spacing={1}>
                    {value.icon && <value.icon sx={{ color: value.color || 'default' }} />}
                    <Typography variant="body1" sx={{ fontWeight: 500, color: value.color || 'default' }}>{value.label}</Typography>
                </Stack>}
            </MenuItem>
        });
    };

    const withAutoComplete = () => {
        return <Autocomplete
            disablePortal
            clearIcon={null}
            disabled={isReadOnly}
            sx={{ '& .MuiInputBase-root': {...sxObject, borderColor: showError ? 'error.main' : undefined}  }}
            options={selectionValues}
            onChange={(event, newValue) => {
                if (newValue) {
                    handleOnChange({ target: { value: newValue.value } } as SelectChangeEvent<string>, newValue.label);
                }
            }}
            renderOption={(props, option) => {
                return <li {...props}>
                    {showChip ? <Chip sx={{ display: 'flex', backgroundColor: option.color}} label={option.label} color='primary'/> : 
                    <Stack direction="row" spacing={1} sx={{p:1}}>
                        {option.icon && <option.icon sx={{ color: option.color }} />}
                        <Typography variant="body1" sx={{ fontWeight: 500, color: option.color }}>{option.label}</Typography>
                    </Stack>}
                </li>
                }}
            renderInput={(params) => <TextField {...params} label={showHeader ? '' : dropDownLabel} />}
        />
    }

    return <><FormControl fullWidth>
        {showHeader && <Typography variant="subtitle1" fontWeight="bold">{dropDownLabel}</Typography>}
        {!showHeader && <InputLabel sx={labelSx}>{dropDownLabel}</InputLabel>}
        {!getAutoComplete ? <Select
            label={showHeader ? '' : dropDownLabel}
            sx={sxObject}
            variant={variant ? variant : 'filled'}
            value={currentValue}
            onChange={handleOnChange}
            disabled={isReadOnly}
            error={showError}
        >
            {getMenuItems()}
        </Select> : withAutoComplete()}

        {showError && <Typography variant="caption" color="error" sx={{ ml:0.3 }}>{helperText}</Typography>}
    </FormControl>
    </>
};

export default DropdownSelection;